'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import React, {
  ReactElement, useCallback, useEffect, useState
} from 'react';
import { useForm } from 'react-hook-form';
import { FiTrash2 } from 'react-icons/fi';
import { z } from 'zod';

import { Button } from '@/components/button/button';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import { useModalKeyboard } from '@/hooks/useModalKeyboard';

import styles from './deleteModal.module.scss';

type DeleteStep = 'confirm' | 'validate' | 'success';

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => Promise<void> | void;
  entityName: string;
  entityType?: string; // e.g., "model", "user", "provider"
  body?: ReactElement;
  overrideWarning?: ReactElement;
}

const createDeleteSchema = ( entityName: string ) => z.object( {
  confirmationText: z.string().refine(
    ( val ) => val === entityName,
    { message: `Please type "${ entityName }" exactly to confirm` }
  )
} );

type DeleteFormData = z.infer<ReturnType<typeof createDeleteSchema>>;

export function DeleteModal( {
  isOpen,
  onClose,
  onDelete,
  entityName,
  entityType = 'item',
  body,
  overrideWarning
}: DeleteModalProps ) {
  const [ step, setStep ] = useState<DeleteStep>( 'confirm' );
  const [ isDeleting, setIsDeleting ] = useState( false );
  const [ error, setError ] = useState( '' );

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm<DeleteFormData>( {
    resolver: zodResolver( createDeleteSchema( entityName ) ),
    mode: 'onChange',
    defaultValues: {
      confirmationText: ''
    }
  } );

  // Reset form when modal closes or entityName changes
  useEffect( () => {
    if ( isOpen ) {
      reset( { confirmationText: '' } );
      setError( '' );
    }
  }, [ isOpen, entityName, reset ] );

  const handleClose = useCallback( () => {
    setStep( 'confirm' );
    reset( { confirmationText: '' } );
    setError( '' );
    setIsDeleting( false );
    onClose();
  }, [ onClose, reset ] );

  const handleConfirm = useCallback( () => {
    setStep( 'validate' );
  }, [] );

  const onSubmit = useCallback( async() => {
    setError( '' );
    setIsDeleting( true );

    try {
      await onDelete();
      setStep( 'success' );
    } catch( err ) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete';
      setError( errorMessage );
    } finally {
      setIsDeleting( false );
    }
  }, [ onDelete ] );

  const handleBack = useCallback( () => {
    setStep( 'confirm' );
    reset( { confirmationText: '' } );
    setError( '' );
  }, [ reset ] );

  const isConfirmDisabled = !isValid || isDeleting;

  const handleEnter = useCallback( () => {
    if ( step === 'confirm' ) handleConfirm();
    else if ( step === 'validate' && !isConfirmDisabled ) handleSubmit( onSubmit )();
    else if ( step === 'success' ) handleClose();
  }, [ step, isConfirmDisabled, handleConfirm, handleSubmit, onSubmit, handleClose ] );

  useModalKeyboard( {
    onEscape: step === 'success' ? handleClose : step === 'validate' ? handleBack : handleClose,
    onEnter: handleEnter,
    enterDisabled: step === 'validate' && isConfirmDisabled
  } );

  if ( !isOpen ) return null;

  return (
    <Modal
      modalType="delete-modal"
      className={ clsx( styles.modal ) }
    >
      <div className={ clsx( styles.modal_content ) }>
        {/* Step 1: Confirmation */}
        {step === 'confirm' && (
          <>
            <div className={ clsx( styles.modal_header ) }>
              <Heading as="h2" size="md" className={ clsx( styles.title ) }>
                Delete {entityType}?
              </Heading>

              {
                overrideWarning || (
                  <Text as="p" size="md">
                    This action will delete <strong>{entityName}</strong> and all associated data.
                  </Text>
                )
              }
            </div>

            {
              body && (
                <div className={ clsx( styles.modal_body ) }>
                  { body }
                </div>
              )
            }

            <div className={ clsx( styles.modal_footer ) }>
              <div className={ clsx( styles.buttons ) }>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={ handleClose }
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onClick={ handleConfirm }
                  icon={ <Icon icon={ FiTrash2 } /> }
                >
                  Delete {entityType}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Step 2: Validation */}
        {step === 'validate' && (
          <>
            <div className={ clsx( styles.modal_header ) }>
              <Heading as="h2" size="md" className={ clsx( styles.title ) }>
                Type {entityType} name to confirm
              </Heading>

              <Text as="p" size="md" className={ clsx( styles.instruction_text ) }>
                To delete <strong>{entityName}</strong>, type the {entityType} name below to confirm. This action cannot be undone.
              </Text>

              <Input
                label={ `${ entityType.charAt( 0 ).toUpperCase() + entityType.slice( 1 ) } name` }
                type="text"
                placeholder={ entityName }
                autoFocus
                { ...register( 'confirmationText' ) }
              />

              {( errors.confirmationText || error ) && (
                <div className={ clsx( styles.error ) }>
                  <Text as="span" size="sm" className={ clsx( styles.error_message ) }>
                    {errors.confirmationText?.message || error}
                  </Text>
                </div>
              )}
            </div>

            <div className={ clsx( styles.modal_footer ) }>
              <div className={ clsx( styles.buttons ) }>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={ handleBack }
                  disabled={ isDeleting }
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onClick={ handleSubmit( onSubmit ) }
                  disabled={ isConfirmDisabled }
                  icon={ <Icon icon={ FiTrash2 } /> }
                >
                  {isDeleting ? 'Deleting...' : `Delete ${ entityType }`}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Step 3: Success */}
        {step === 'success' && (
          <>
            <div className={ clsx( styles.modal_body_success ) }>
              <Text as="p" size="md" className={ clsx( styles.success_text ) }>
                <strong>{entityName}</strong> has been deleted.
              </Text>
            </div>

            <div className={ clsx( styles.modal_footer ) }>
              <div className={ clsx( styles.buttons_right ) }>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={ handleClose }
                >
                  Close
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
